import { useMemo, useState } from 'react'
import { PhoneVerification, type EtapeCode } from '@/components/onboarding/PhoneVerification'
import { useNavigate, useParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  ArrowRight,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  MessageSquare,
  Phone,
  Shield,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { defaultLocale } from '@/lib/locales'
import { ApiHttpError } from '@/api/auth'
import {
  acceptAgentInvitation,
  fetchAgentInvitation,
  sendInvitationPhoneCode,
  verifyInvitationPhoneCode,
} from '@/api/agentInvitation'
import { useAuthStore } from '@/stores/useAuthStore'
import { clearAllAuthData } from '@/lib/auth'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

// The invitee reads this page in the language of the invitation link (/fr or /en)
const setupSchema = (t: TFunction) => z
  .object({
    first_name: z.string().min(1, t('agentSetup.prenomRequis')),
    last_name: z.string().min(1, t('agentSetup.nomRequis')),
    phone_number: z.string().min(8, t('agentSetup.cellulaireRequis')),
    password: z.string().min(6, t('agentSetup.motDePasseCourt')),
    confirm_password: z.string().min(1, t('agentSetup.confirmationRequise')),
  })
  .refine((data) => data.password === data.confirm_password, {
    path: ['confirm_password'],
    message: t('agentSetup.differents'),
  })

type SetupForm = z.infer<ReturnType<typeof setupSchema>>

const FEATURES = [
  { icon: Phone, title: 'agentSetup.f1Titre', desc: 'agentSetup.f1Texte' },
  { icon: MessageSquare, title: 'agentSetup.f2Titre', desc: 'agentSetup.f2Texte' },
  { icon: Clock, title: 'agentSetup.f3Titre', desc: 'agentSetup.f3Texte' },
  { icon: Users, title: 'agentSetup.f4Titre', desc: 'agentSetup.f4Texte' },
] as const

export function AgentSetupPage() {
  const { t } = useTranslation()
  const { locale = defaultLocale, token = '' } = useParams<{ locale: string; token: string }>()
  const navigate = useNavigate()
  useDocumentTitle(t('agentSetup.titrePage'))
  const schema = useMemo(() => setupSchema(t), [t])
  const dateLocale = locale === 'en' ? 'en-CA' : 'fr-CA'

  const [showPassword, setShowPassword] = useState(false)
  // Vérification du cellulaire par code, dans le formulaire, avant la création du compte.
  const [etapeCellulaire, setEtapeCellulaire] = useState<EtapeCode>('idle')
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)

  const invitationQuery = useQuery({
    queryKey: ['agent-invitation', token],
    queryFn: () => fetchAgentInvitation(token),
    enabled: !!token,
    retry: false,
  })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetupForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone_number: '',
      password: '',
      confirm_password: '',
    },
  })

  const acceptMutation = useMutation({
    mutationFn: async (values: SetupForm) =>
      acceptAgentInvitation({
        token,
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        phone_number: values.phone_number?.trim() || '',
        password: values.password,
        confirm_password: values.confirm_password,
      }),
    onSuccess: (data) => {
      clearAllAuthData()
      setTokens(data.tokens.access_token, data.tokens.refresh_token)
      setUser({
        id: data.user_id,
        email: data.email,
        role: 'AGENT',
        companyId: data.company_id,
        companyName: data.company_name,
      })
      toast.success(t('toasts.signup.accountCreated'))
      // Le garde d'onboarding enverra d'abord vérifier le cellulaire.
      setTimeout(() => navigate(`/${locale}/dashboard`, { replace: true }), 1200)
    },
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        const data = error.data as Record<string, unknown>
        const message =
          (typeof data?.error === 'string' && data.error) ||
          (typeof data?.detail === 'string' && data.detail) ||
          t('agentSetup.impossible')
        toast.error(message)
        return
      }
      console.error('[AgentSetup]', error)
      toast.error(t('toasts.common.serverError'))
    },
  })

  const onSubmit = handleSubmit((values) => acceptMutation.mutate(values))
  const isSubmitting = acceptMutation.isPending

  if (invitationQuery.isLoading) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-lg">{t('agentSetup.chargement')}</span>
        </div>
      </div>
    )
  }

  if (invitationQuery.isError || !invitationQuery.data) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('agentSetup.introuvableTitre')}</h2>
            <p className="text-slate-500 mb-6">{t('agentSetup.introuvableTexte')}</p>
            <Button
              onClick={() => navigate(`/${locale}`)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {t('agentSetup.retourAccueil')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const invitation = invitationQuery.data

  if (invitation.is_expired) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('agentSetup.expireeTitre')}</h2>
            <p className="text-slate-500 mb-6">
              {t('agentSetup.expireeTexte', {
                date: new Date(invitation.expires_at).toLocaleDateString(dateLocale),
                manager: invitation.manager_name,
              })}
            </p>
            <Button
              onClick={() => navigate(`/${locale}`)}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {t('agentSetup.retourAccueil')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-dvh w-full grid grid-cols-1 lg:grid-cols-5 bg-white text-slate-900">
      <div className="hidden lg:flex lg:col-span-2 flex-col justify-between bg-[#0A192F] text-white p-10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(800px 400px at 20% 10%, #ffffff 0%, transparent 60%)' }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <img
            src="https://sdk.callizy.ca/assets/favicon_callizy.png"
            alt="Callizy"
            className="h-16 w-16"
          />
          <span className="text-white font-bold text-xl">Callizy</span>
        </div>

        <div className="relative z-10 space-y-6 max-w-md">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white" /> Invitation
          </div>

          <div>
            <h1 className="text-3xl font-semibold leading-tight mb-3">
              {t('agentSetup.rejoignez', { company: invitation.company_name })}
            </h1>
            <p className="text-white/70 text-lg">{t('agentSetup.chapo')}</p>
          </div>

          <div className="space-y-3 pt-2">
            {FEATURES.map((feature) => {
              const Icon = feature.icon
              return (
                <div key={feature.title} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{t(feature.title)}</p>
                    <p className="text-white/60 text-xs">{t(feature.desc)}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* html/body/#root sont en overflow hidden (app plein écran) : cette colonne défile seule. */}
      <div className="lg:col-span-3 h-dvh overflow-y-auto flex items-start lg:items-center justify-center px-6 py-10 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('agentSetup.creerCompte')}</h1>
            <p className="text-slate-500">
              <Trans
                i18nKey="agentSetup.remplissez"
                values={{ company: invitation.company_name }}
                components={{ strong: <strong className="text-slate-700" /> }}
              />
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
                  {t('agentSetup.prenom')}
                </Label>
                <Input
                  id="first_name"
                  placeholder={t('agentSetup.prenomExemple')}
                  autoComplete="given-name"
                  aria-invalid={!!errors.first_name}
                  disabled={isSubmitting}
                  className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  {...register('first_name')}
                />
                {errors.first_name && (
                  <p className="text-xs text-red-600">{errors.first_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
                  {t('agentSetup.nom')}
                </Label>
                <Input
                  id="last_name"
                  placeholder={t('agentSetup.nomExemple')}
                  autoComplete="family-name"
                  aria-invalid={!!errors.last_name}
                  disabled={isSubmitting}
                  className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  {...register('last_name')}
                />
                {errors.last_name && (
                  <p className="text-xs text-red-600">{errors.last_name.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">{t('agentSetup.courriel')}</Label>
              <Input
                value={invitation.email}
                disabled
                className="h-11 bg-slate-100 text-slate-500 border-slate-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number" className="text-sm font-medium text-slate-700">
                {t('onboarding.welcome.verif.field', 'Cellulaire')}
                {etapeCellulaire === 'ok' && <span className="ml-2 text-xs font-medium text-emerald-600">{t('onboarding.welcome.verif.verified')}</span>}
              </Label>
              <Input
                id="phone_number"
                type="tel"
                placeholder="+1 555 123 4567"
                autoComplete="tel"
                disabled={isSubmitting || etapeCellulaire !== 'idle'}
                className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                {...register('phone_number')}
              />
              <PhoneVerification
                phone={watch('phone_number') || ''}
                locale={locale}
                etape={etapeCellulaire}
                onEtape={setEtapeCellulaire}
                envoyer={(phone, lang) => sendInvitationPhoneCode(token, phone, lang)}
                verifier={(code) => verifyInvitationPhoneCode(token, code)}
                sombre={false}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                {t('agentSetup.motDePasse')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('agentSetup.motDePasseAide')}
                  autoComplete="new-password"
                  aria-invalid={!!errors.password}
                  disabled={isSubmitting}
                  className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500 pr-11"
                  {...register('password')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-sm font-medium text-slate-700">
                {t('agentSetup.confirmer')}
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('agentSetup.confirmerAide')}
                  autoComplete="new-password"
                  aria-invalid={!!errors.confirm_password}
                  disabled={isSubmitting}
                  className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500 pr-11"
                  {...register('confirm_password')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-slate-400 hover:text-slate-600"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.confirm_password && (
                <p className="text-xs text-red-600">{errors.confirm_password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base shadow-lg shadow-blue-500/25 transition-all hover:shadow-blue-500/40 mt-2"
              disabled={isSubmitting || etapeCellulaire !== 'ok'}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('agentSetup.creation')}
                </>
              ) : (
                <>
                  {t('agentSetup.creerMonCompte')}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              {t('agentSetup.envoyeeA')}{' '}
              <span className="font-medium text-slate-500">{invitation.email}</span>
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {t('agentSetup.expireLe', {
                date: new Date(invitation.expires_at).toLocaleDateString(dateLocale, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
